if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (this.dataset.desktopLayout === 'carousel') {
          const colourData = this.querySelector('[data-gallery-colour-order]');
          this.colourOrder = new Map();
          if (colourData) {
            const { order, leads } = JSON.parse(colourData.textContent);
            let group = order[0];
            order.forEach((id, index) => {
              if (leads.includes(id)) group = id;
              this.colourOrder.set(String(id), { group, index });
            });
            const activeMedia = this.elements.viewer.querySelector('.product__media-item.is-active');
            if (activeMedia) this.prioritizeColour(activeMedia);
          }
          this.querySelectorAll('[data-gallery-step]').forEach((button) => {
            button.addEventListener('click', () => {
              const items = Array.from(this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]'));
              const index = items.findIndex((item) => item.classList.contains('is-active'));
              const next = (index + Number(button.dataset.galleryStep) + items.length) % items.length;
              this.setActiveMedia(items[next].dataset.mediaId, false);
            });
          });
          this.elements.viewer.addEventListener('slideChanged', (event) => {
            if (this.mql.matches || !event.detail.currentElement) return;
            this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]').forEach((item) => {
              item.classList.toggle('is-active', item === event.detail.currentElement);
            });
            this.updateCarouselCounter();
          });
          this.updateCarouselCounter();
        }
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      prioritizeColour(activeMedia) {
        const getOrder = (id) => this.colourOrder.get(id.split('-').pop());
        const selected = getOrder(activeMedia.dataset.mediaId);
        if (!selected) return;
        const reorder = (items, key) => {
          const original = Array.from(items);
          const ordered = original.slice().sort((a, b) => {
            const first = getOrder(a.dataset[key]);
            const second = getOrder(b.dataset[key]);
            if (!first || !second) return 0;
            return Number(second.group === selected.group) - Number(first.group === selected.group) || first.index - second.index;
          });
          if (ordered.some((item, index) => item !== original[index])) {
            ordered.forEach((item) => item.parentElement.append(item));
          }
          return ordered;
        };
        reorder(this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]'), 'mediaId');
        if (this.elements.thumbnails) {
          reorder(this.elements.thumbnails.querySelectorAll('[data-target]'), 'target').forEach((item, index) => {
            item.dataset.mediaPosition = index + 1;
          });
          this.elements.thumbnails.resetPages();
        }
        this.elements.viewer.slider?.scrollTo({ left: 0, behavior: 'instant' });
        this.elements.viewer.resetPages();
      }

      updateCarouselCounter() {
        const counter = this.querySelector('[data-gallery-current]');
        if (!counter) return;
        const items = Array.from(this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]'));
        counter.textContent = items.findIndex((item) => item.classList.contains('is-active')) + 1;
        this.querySelector('[data-gallery-total]').textContent = items.length;
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          if (this.dataset.desktopLayout === 'carousel' && this.colourOrder?.size) {
            this.prioritizeColour(activeMedia);
          } else {
            activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);
            if (this.elements.thumbnails) {
              const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
              activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);
        if (this.dataset.desktopLayout === 'carousel') this.updateCarouselCounter();

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
